/**
 * RhythmLogic.js
 * Utilities for dynamic meter handling, beat strength calculation, and grid locking.
 */

/**
 * Calculates the "Metric Strength" of a given beat position within a bar.
 * Used to drive probability of note onsets, harmonic choices, and velocity accents.
 *
 * Time Base: Quarter Note = 1.0
 *
 * @param {number} beat - The beat position within the bar (e.g., 0.0, 1.5, 3.0).
 * @param {Array} timeSig - Time Signature [Numerator, Denominator] (e.g., [4, 4], [6, 8]).
 * @returns {number} Strength value between 0.0 (Weak) and 1.0 (Strong).
 */
export const getBeatStrength = (beat, timeSig = [4, 4]) => {
    const num = timeSig[0];
    const denom = timeSig[1];

    // Normalize beat to within the bar (just in case)
    // Bar duration in quarters = (num / denom) * 4
    const barDuration = (num / denom) * 4;
    const pos = beat % barDuration;

    // Tolerance for floating point comparison
    const isAt = (target) => Math.abs(pos - target) < 0.05;

    // --- 4/4 ---
    if (num === 4 && denom === 4) {
        if (isAt(0.0)) return 1.0; // Downbeat
        if (isAt(2.0)) return 0.8; // Backbeat / Half-way
        if (isAt(1.0) || isAt(3.0)) return 0.5; // Weak quarters
        if (pos % 1.0 !== 0) return 0.2; // Off-beats (8ths/16ths)
        return 0.1;
    }

    // --- 3/4 (Waltz) ---
    if (num === 3 && denom === 4) {
        if (isAt(0.0)) return 1.0; // Downbeat
        if (isAt(1.0) || isAt(2.0)) return 0.5; // Weak quarters
        if (pos % 1.0 !== 0) return 0.2;
        return 0.1;
    }

    // --- 6/8 (Compound Duple) ---
    if (num === 6 && denom === 8) {
        // Bar is 3.0 quarters long (6 * 0.5)
        // Strong beats: 1 (0.0) and 4 (1.5)
        if (isAt(0.0)) return 1.0;
        if (isAt(1.5)) return 0.9;
        // Weak eighths
        if (pos % 0.5 === 0) return 0.4;
        return 0.1;
    }

    // --- 5/4 (Complex) ---
    if (num === 5 && denom === 4) {
        // Default to 3+2 split
        if (isAt(0.0)) return 1.0;
        if (isAt(3.0)) return 0.8; // Secondary strong beat
        if (pos % 1.0 === 0) return 0.5; // Other quarters
        return 0.2;
    }

    // --- Generic Fallback ---
    // Downbeat always strong
    if (isAt(0.0)) return 1.0;
    // On-beat quarters
    if (pos % 1.0 === 0) return 0.6;
    // Off-beats
    return 0.2;
};

/**
 * Generates a Euclidean Rhythm pattern (Bjorklund's algorithm).
 * Distributes 'pulses' as evenly as possible over 'steps'.
 *
 * @param {number} steps - Total steps in the cycle (e.g., 16 for a bar of 16ths).
 * @param {number} pulses - Number of active hits (e.g., 4).
 * @param {number} rotate - Phase shift (rotation) of the pattern.
 * @returns {Array<number>} Binary array of length 'steps' (1=Hit, 0=Rest).
 */
export const generateEuclideanRhythm = (steps, pulses, rotate = 0) => {
    if (pulses >= steps) return new Array(steps).fill(1);
    if (pulses <= 0) return new Array(steps).fill(0);

    let pattern = [];
    let counts = [];
    let remainders = [];
    let divisor = steps - pulses;
    remainders.push(pulses);
    let level = 0;

    while (true) {
        counts.push(Math.floor(divisor / remainders[level]));
        remainders.push(divisor % remainders[level]);
        divisor = remainders[level];
        level++;
        if (remainders[level] <= 1) break;
    }
    counts.push(divisor);

    const build = (level) => {
        if (level === -1) return [0];
        if (level === -2) return [1];

        let result = [];
        for (let i = 0; i < counts[level]; i++) {
            result = result.concat(build(level - 1));
        }
        if (remainders[level] !== 0) {
            result = result.concat(build(level - 2));
        }
        return result;
    };

    pattern = build(level);

    // Reverse to get the standard Euclidean form?
    // Bjorklund implementations vary.
    // Standard approach: pulses at front.
    // This implementation is recursive.

    // Alternative simpler iterative implementation for stability:
    // (k, n) -> Bresenham line algorithm approximation
    pattern = [];
    let bucket = 0;
    for (let i = 0; i < steps; i++) {
        bucket += pulses;
        if (bucket >= steps) {
            bucket -= steps;
            pattern.push(1);
        } else {
            pattern.push(0);
        }
    }
    // Rotate
    const offset = rotate % steps;
    if (offset > 0) {
        pattern = [...pattern.slice(steps - offset), ...pattern.slice(0, steps - offset)];
    }

    return pattern;
};

/**
 * Converts a binary pattern into Abstract Events.
 *
 * @param {Array<number>} pattern - Binary pattern (1=Hit, 0=Rest).
 * @param {number} barStart - Absolute time of bar start.
 * @param {number} stepSize - Duration of one step in beats (e.g., 0.25 for 16ths).
 * @param {number} legato - Note length as percentage of step (0.0-1.0+).
 * @param {Object} options - { microMutation: 0.0-1.0, random: RNG }
 * @returns {Array<Object>} List of events { s, l }.
 */
export const patternToEvents = (pattern, barStart, stepSize = 0.25, legato = 0.8, options = {}) => {
    const events = [];
    const mutationRate = options.microMutation || 0.0;
    const random = options.random;

    pattern.forEach((isHit, idx) => {
        if (isHit) {
            let start = idx * stepSize;
            let length = stepSize * legato;

            // Task 2: Entropy Injection (Micro-Mutation)
            if (mutationRate > 0 && random && random.bool(mutationRate)) {
                // Type 1: Shift Start Time (Humanize/Syncopate)
                // Shift by +/- half step
                if (random.bool(0.5)) {
                    const shift = (random.bool(0.5) ? 0.5 : -0.5) * stepSize;
                    start += shift;
                }
                // Type 2: Length Variation
                else {
                    length *= (random.bool(0.5) ? 0.5 : 1.5);
                }
            }

            // Clamp start to avoid negative relative time?
            // Actually, negative start might be valid pickup, but let's clamp for safety.
            if (start < 0) start = 0;

            events.push({
                s: start,
                l: length
            });
        }
    });
    return events;
};

/**
 * Returns a phrase pattern array (indices for rotation/variation).
 * @param {string} type - 'AAAB', 'AABA', 'ABAB', 'ABCD'
 * @param {number} length - Length of phrase (usually 4 or 8)
 * @returns {Array<number>} e.g., AAAB -> [0, 0, 0, 1]
 */
export const getPhrasePattern = (type, length = 4) => {
    // Basic types map to [0, 1, 2, 3] indices
    // 0 = A, 1 = B, 2 = C, 3 = D
    let template = [];
    switch (type) {
        case 'AAAB': template = [0, 0, 0, 1]; break;
        case 'AABA': template = [0, 0, 1, 0]; break;
        case 'ABAB': template = [0, 1, 0, 1]; break;
        case 'ABAC': template = [0, 1, 0, 2]; break; // Good for lyrical
        case 'ABCD': template = [0, 1, 2, 3]; break; // Through-composed
        default: template = [0, 0, 0, 1];
    }

    // Expand if length > 4 (Simple repetition for now)
    const result = [];
    for (let i = 0; i < length; i++) {
        result.push(template[i % template.length]);
    }
    return result;
};

/**
 * Deterministically selects a phrase type based on seed and section.
 */
export const getProceduralPhraseType = (seed, sectionType) => {
    // Simple hash
    let hash = 0;
    const str = (seed || 'default') + (sectionType || 'verse');
    for (let i = 0; i < str.length; i++) {
        hash = ((hash << 5) - hash) + str.charCodeAt(i);
        hash |= 0;
    }
    const absHash = Math.abs(hash);

    // Weighted choice
    const roll = absHash % 100;
    if (sectionType.toLowerCase().includes('chorus')) {
        // Choruses tend to be repetitive/catchy (AAAB or ABAB)
        if (roll < 40) return 'AAAB';
        if (roll < 70) return 'ABAB';
        if (roll < 90) return 'AABA';
        return 'ABCD';
    } else if (sectionType.toLowerCase().includes('verse')) {
        // Verses can be more narrative (AABA, ABAC)
        if (roll < 30) return 'AABA';
        if (roll < 60) return 'ABAC';
        if (roll < 80) return 'AAAB';
        return 'ABCD';
    }

    return 'AAAB'; // Default
};
